import type { Recommendation, SiteRagReport } from '../../types/siteRag';
import type { AdaptiveProjectIntentOverviewModel } from '../projectIntentOverviewModel';

const WATER_MARKER = '\n\nModeled phase water screening\n';
const WATER_END =
  '\nThese cumulative phase cases and alternative cooling cases are not additive. They describe data-center water only; separate power-plant process water, utility supply commitments and permitted withdrawals require their own evidence.';
const STAFFING_MARKER = '\n\nModeled phase staffing\n';

export interface ParsedPhaseWaterScenarioRow {
  label: string;
  applicability: string;
  boundary: string;
  flowBasis: string;
  averageDay: string | null;
  annual: string | null;
  fullLoad: string | null;
  conditionalNote: string | null;
  assistSummary: string | null;
  refs: string;
}

export interface ParsedPhaseWaterCase {
  scope: string;
  loadConversion: string | null;
  effectiveThermalLoad: string | null;
  rows: ParsedPhaseWaterScenarioRow[];
  sourceRefs: string;
}

export interface ParsedPhaseStaffingSummary {
  estimates: string[];
  sourceRefs: string;
}

export interface ParsedActionPlanPhase {
  hasStructuredDisclosure: boolean;
  leadNarrative: string;
  waterCases: ParsedPhaseWaterCase[];
  staffing: ParsedPhaseStaffingSummary | null;
  supportingDisclosureText: string | null;
}

export interface ParsedRecommendationCard {
  hasSupportingDetail: boolean;
  summaryDescription: string;
  supportingDetail: string | null;
  structuredMeta: Array<{ label: string; value: string }>;
}

export interface RecommendationPresentationContext {
  siteId?: string;
  reportId?: string;
  sedId?: string;
  references?: ReadonlyArray<{
    documentId?: string;
    field?: string;
    source?: string;
    url?: string;
    citedInThisReport?: boolean;
  }>;
  developmentPathway?: ReadonlyArray<{
    phase?: string;
    title?: string;
    description?: string;
    cod?: string;
    capacityMW?: string;
  }>;
}

/** Deduplicate `[Ref N]` tokens in a string while preserving order of first appearance. */
export function dedupeRefTokensInText(text: string): string {
  // Collapse runs of `[Ref N] [Ref M] ...` so duplicate refs in the same run are removed
  return text.replace(/(?:\[Ref \d+\](?:\s*,?\s*\[Ref \d+\])+)/g, (match) => {
    const seen = new Set<string>();
    const tokens: string[] = [];
    for (const m of match.matchAll(/\[Ref \d+\]/g)) {
      if (!seen.has(m[0])) {
        seen.add(m[0]);
        tokens.push(m[0]);
      }
    }
    return tokens.join(' ');
  });
}

function extractUniqueRefs(text: string): string {
  const seen = new Set<string>();
  const refs: string[] = [];
  for (const m of text.matchAll(/\[Ref \d+\]/g)) {
    if (!seen.has(m[0])) {
      seen.add(m[0]);
      refs.push(m[0]);
    }
  }
  return refs.join(' ');
}

function stripTrailingRefs(line: string): { body: string; refs: string } {
  const refs = extractUniqueRefs(line);
  const body = line.replace(/(?:\s*\[Ref \d+\])+\s*$/g, '').trim();
  return { body, refs };
}

function parseWaterScreeningBlock(waterBlock: string): ParsedPhaseWaterCase[] {
  const lines = waterBlock
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);

  const cases: ParsedPhaseWaterCase[] = [];
  let current: ParsedPhaseWaterCase | null = null;

  for (const rawLine of lines) {
    const { body, refs } = stripTrailingRefs(rawLine);

    // Case header: `Modeled <scope>.` (not a scenario row which has `(<applicability>; <boundary>; <flowBasis>):`)
    if (
      body.startsWith('Modeled ') &&
      !body.includes('): ') &&
      body.endsWith('.')
    ) {
      current = {
        scope: body.slice('Modeled '.length, -1).trim(),
        loadConversion: null,
        effectiveThermalLoad: null,
        rows: [],
        sourceRefs: refs,
      };
      cases.push(current);
      continue;
    }

    if (!current) continue;

    if (body.startsWith('Load conversion:')) {
      current.loadConversion = body.slice('Load conversion:'.length).trim().replace(/\.$/, '');
      if (refs && !current.sourceRefs) current.sourceRefs = refs;
      continue;
    }

    if (body.startsWith('Effective thermal load:')) {
      current.effectiveThermalLoad = body.slice('Effective thermal load:'.length).trim().replace(/\.$/, '');
      if (refs && !current.sourceRefs) current.sourceRefs = refs;
      continue;
    }

    // Scenario row: `Modeled <label> (<applicability>; <boundary>; <flowBasis>): <body>.`
    const rowMatch = /^Modeled\s+(.+?)\s+\(([^;]+);\s*([^;]+);\s*([^)]+)\):\s*(.+?)\.?$/.exec(body);
    if (rowMatch) {
      const [, label, applicability, boundary, flowBasis, metricsPart] = rowMatch;
      let averageDay: string | null = null;
      let annual: string | null = null;
      let fullLoad: string | null = null;
      let conditionalNote: string | null = null;

      if (metricsPart.startsWith('potentially ')) {
        conditionalNote = metricsPart;
      } else {
        for (const segment of metricsPart.split(';').map((s) => s.trim())) {
          if (segment.startsWith('average day ')) {
            averageDay = segment.slice('average day '.length).trim();
          } else if (segment.startsWith('annual ')) {
            annual = segment.slice('annual '.length).trim();
          } else if (segment.startsWith('100%-load cooling sensitivity ')) {
            fullLoad = segment.slice('100%-load cooling sensitivity '.length).trim();
          }
        }
      }

      current.rows.push({
        label: label.trim(),
        applicability: applicability.trim(),
        boundary: boundary.trim(),
        flowBasis: flowBasis.trim(),
        averageDay,
        annual,
        fullLoad,
        conditionalNote,
        assistSummary: null,
        refs,
      });
      continue;
    }

    // Adiabatic assist line immediately following a dry cooling row
    if (body.startsWith('Adiabatic assist on the dry path') && current.rows.length > 0) {
      const lastRow = current.rows[current.rows.length - 1]!;
      const colonIdx = body.indexOf(':');
      const assistMetrics = colonIdx >= 0 ? body.slice(colonIdx + 1).trim().replace(/\.$/, '') : body;
      lastRow.assistSummary = assistMetrics;
      if (refs) {
        lastRow.refs = extractUniqueRefs(`${lastRow.refs} ${refs}`);
      }
      continue;
    }

    if (body.startsWith('Methodology and benchmark sources:') && refs) {
      current.sourceRefs = extractUniqueRefs(`${current.sourceRefs} ${refs}`);
    }
  }

  return cases.filter((c) => c.rows.length > 0);
}

function parseStaffingBlock(staffingBlock: string): ParsedPhaseStaffingSummary | null {
  const lines = staffingBlock
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
  const estimates: string[] = [];
  let sourceRefs = '';

  for (const rawLine of lines) {
    const { body, refs } = stripTrailingRefs(rawLine);
    if (body.startsWith('Modeled ') && body.includes('interpretation:')) {
      estimates.push(body);
      if (refs) sourceRefs = extractUniqueRefs(`${sourceRefs} ${refs}`);
    } else if (body.startsWith('Methodology and benchmark sources:')) {
      const inlineRefs = extractUniqueRefs(rawLine);
      if (inlineRefs) sourceRefs = extractUniqueRefs(`${sourceRefs} ${inlineRefs}`);
    }
  }

  if (estimates.length === 0) return null;
  return { estimates, sourceRefs };
}

export function parseActionPlanPhaseDescription(description: string | undefined | null): ParsedActionPlanPhase {
  const raw = (description ?? '').trim();
  if (!raw) {
    return {
      hasStructuredDisclosure: false,
      leadNarrative: '',
      waterCases: [],
      staffing: null,
      supportingDisclosureText: null,
    };
  }

  const waterIdx = raw.indexOf(WATER_MARKER);
  const staffingIdx = raw.indexOf(STAFFING_MARKER);

  if (waterIdx < 0 && staffingIdx < 0) {
    return {
      hasStructuredDisclosure: false,
      leadNarrative: dedupeRefTokensInText(raw),
      waterCases: [],
      staffing: null,
      supportingDisclosureText: null,
    };
  }

  const firstMarkerIdx =
    waterIdx >= 0 && staffingIdx >= 0
      ? Math.min(waterIdx, staffingIdx)
      : waterIdx >= 0
        ? waterIdx
        : staffingIdx;

  const leadNarrative = dedupeRefTokensInText(raw.slice(0, firstMarkerIdx).trim());
  const supportingDisclosureText = raw.slice(firstMarkerIdx).trim();

  let waterCases: ParsedPhaseWaterCase[] = [];
  if (waterIdx >= 0) {
    const waterContentStart = waterIdx + WATER_MARKER.length;
    const waterEndIdx = raw.indexOf(WATER_END, waterContentStart);
    const waterBlockEnd =
      waterEndIdx >= 0
        ? waterEndIdx
        : staffingIdx > waterIdx
          ? staffingIdx
          : raw.length;
    const waterBlock = raw.slice(waterContentStart, waterBlockEnd);
    waterCases = parseWaterScreeningBlock(waterBlock);
  }

  let staffing: ParsedPhaseStaffingSummary | null = null;
  if (staffingIdx >= 0) {
    const staffingContentStart = staffingIdx + STAFFING_MARKER.length;
    const staffingBlock = raw.slice(staffingContentStart);
    staffing = parseStaffingBlock(staffingBlock);
  }

  return {
    hasStructuredDisclosure: true,
    leadNarrative,
    waterCases,
    staffing,
    supportingDisclosureText,
  };
}

function splitRecommendationSentences(text: string): string[] {
  const trimmed = text.trim();
  if (!trimmed) return [];
  const parts: string[] = [];
  const boundaryRe =
    /([.!?](?:["')]|\s*\[(?:(?:verified|inferred|user|context)\s+)?Ref\s+\d+\])*)\s+(?=[A-Z])/g;
  let lastIdx = 0;
  let match: RegExpExecArray | null;
  while ((match = boundaryRe.exec(trimmed)) !== null) {
    const endOfSentence = match.index + match[1]!.length;
    const prefix = trimmed.slice(Math.max(0, match.index - 12), match.index + 1);
    if (
      /\b(?:vs|e\.g|i\.e|cf|etc|approx|est|no|fig|vol|inc|llc|ltd|corp|co|st|dr|mr|ms)\.$/i.test(
        prefix,
      )
    ) {
      continue;
    }
    parts.push(trimmed.slice(lastIdx, endOfSentence).trim());
    lastIdx = boundaryRe.lastIndex;
  }
  const tail = trimmed.slice(lastIdx).trim();
  if (tail) parts.push(tail);
  return parts.length > 0 ? parts : [trimmed];
}

const LONGSPUR_SITE_ID = 'site-1783523395000-bczk3g';
const LONGSPUR_REPORT_ID = 'feasibility-1792773672702-2l6jqi';
const LONGSPUR_SED_ID = 'sed-1792748376299-duxfla';

const NEGATED_OR_COMPARATIVE_RE =
  /\b(?:do\s+not\s+apply|do\s+not\s+describe|not\s+describe\s+this|example\s+do\s+not|unlike\s+the)\b/i;

function extractCitedRefNumbers(text: string): Set<number> {
  const nums = new Set<number>();
  for (const m of text.matchAll(/\[(?:(?:verified|inferred|user|context)\s+)?Ref\s+(\d+)\]/gi)) {
    const n = Number(m[1]);
    if (Number.isInteger(n) && n >= 1) nums.add(n);
  }
  return nums;
}

function buildSourceBackedLongspurRecommendationMeta(
  rec: Recommendation,
  rawDesc: string,
  rawRationale: string,
  context?: RecommendationPresentationContext,
): Array<{ label: string; value: string }> {
  if (!context) return [];
  if (context.siteId !== LONGSPUR_SITE_ID || context.reportId !== LONGSPUR_REPORT_ID) {
    return [];
  }
  if (context.sedId && context.sedId !== LONGSPUR_SED_ID) {
    return [];
  }
  if (!Array.isArray(context.references) || context.references.length === 0) {
    return [];
  }

  const combined = `${rec.title ?? ''} ${rawDesc} ${rawRationale}`;
  if (!combined.trim() || NEGATED_OR_COMPARATIVE_RE.test(combined)) {
    return [];
  }

  const citedNums = extractCitedRefNumbers(`${rawDesc} ${rawRationale}`);
  if (citedNums.size === 0) return [];
  const citedRefs = [...citedNums]
    .map((n) => context.references?.[n - 1])
    .filter((r): r is NonNullable<typeof r> => Boolean(r));
  if (citedRefs.length === 0) return [];

  const hasCitedDoc = (docId: string) => citedRefs.some((r) => r.documentId === docId);
  const hasCitedField = (field: string) => citedRefs.some((r) => r.field === field);
  const pathwayPhases = context.developmentPathway ?? [];
  const hasPhase1And2 =
    pathwayPhases.some((p) => /^Phase\s*1\b/i.test(p.phase ?? p.title ?? '')) &&
    pathwayPhases.some((p) => /^Phase\s*2\b/i.test(p.phase ?? p.title ?? ''));
  const hasWorkStages = pathwayPhases.some((p) => /^Work\s*stage\s*[12]\b/i.test(p.phase ?? p.title ?? ''));

  const recType = (rec.type ?? '').toLowerCase();
  const recTitle = (rec.title ?? '').trim();

  if (
    recType === 'power' &&
    recTitle === 'Advance Dual-Track 345 kV Grid Interconnection and BTM Gas Bridge' &&
    hasCitedDoc('doc-1791195957188-1zhigb') &&
    hasCitedDoc('doc-1791196017643-lmaljw') &&
    hasCitedDoc('doc-1791196058507-xv0jex') &&
    hasCitedDoc('doc-1791195991451-m61ksm')
  ) {
    const out: Array<{ label: string; value: string }> = [];
    if (hasPhase1And2) {
      out.push({ label: 'Phase', value: 'Phase 1 – Phase 2 (Pre-FID)' });
    }
    out.push({ label: 'Suggested priority', value: 'Critical (P0 Pre-FID Gate — suggestion)' });
    out.push({
      label: 'Suggested role (unassigned)',
      value: 'Power & Interconnection Lead / Utility & Gas Counsel (suggestion)',
    });
    if (hasCitedField('electricity') && hasCitedField('fuelSupply')) {
      out.push({
        label: 'Deliverable',
        value:
          'Executed Tri-State HIL/OATT study & IA (with grid-mode/protection clearance), binding firm gas lateral/transport/supply contracts (with verified delivery pressure, fuel quality/heating value [Btu/scf & Wobbe index], hourly/daily deliverability, schedule & curtailment terms), OEM site-rated N-1/transient design, and matched-boundary all-in LCOE model',
      });
    }
    out.push({
      label: 'Dependency',
      value:
        'Tri-State MPR-26-HW05 HIL filing ($250k study fee + security), Black Hills / Tallgrass pressure & thermal-unit/heating-value reconciliation, plus co-gating Laramie County data-center/BTM generation zoning, WDEQ air & WY PSC determinations, verified flood-free/wetland/geotech/ESA buildable pad, and ASHRAE/OEM peak thermal & water-supply/rights clearance (see Conditions for proceeding & Recs. 2–5)',
    });
    if (hasPhase1And2) {
      out.push({
        label: 'Decision unlocked',
        value:
          'Phase 1 BTM bridge procurement & Phase 2 345 kV transmission construction commitment (co-gated with site/flood, permitting & water/thermal clearances)',
      });
    }
    out.push({ label: 'Status', value: 'Open — Pre-FID Gate' });
    return out;
  }

  if (
    recType === 'cooling' &&
    (recTitle === 'Select Dry Coolers with Adiabatic Assist and Verify Water Supply Source' ||
      recTitle ===
        'Evaluate Dry Coolers with Adiabatic Assist as Candidate Architecture and Verify Water Supply Source') &&
    hasCitedField('climate') &&
    hasCitedField('waterStress') &&
    hasCitedDoc('doc-1791196149217-bqcubw')
  ) {
    const out: Array<{ label: string; value: string }> = [];
    if (hasPhase1And2) {
      out.push({ label: 'Phase', value: 'Phase 1 – Phase 2 (Pre-FID)' });
    }
    out.push({ label: 'Suggested priority', value: 'High (P1 Pre-FID Gate — suggestion)' });
    out.push({
      label: 'Suggested role (unassigned)',
      value: 'Mechanical / Thermal Lead, Hydrogeologist & Water-Rights Counsel (suggestion)',
    });
    out.push({
      label: 'Deliverable',
      value:
        'Verified municipal/groundwater supply will-serve & water-rights opinion plus ASHRAE peak thermal & OEM derating basis of design',
    });
    out.push({
      label: 'Dependency',
      value:
        'Candidate municipal or Laramie County Groundwater Control Area source identification & multi-year ASHRAE design weather data',
    });
    out.push({
      label: 'Decision unlocked',
      value: 'Final heat-rejection architecture selection (dry vs. adiabatic-assist) and binding water-supply contracting',
    });
    out.push({ label: 'Status', value: 'Open — Pre-FID Gate' });
    return out;
  }

  if (
    recType === 'site' &&
    recTitle === 'Commission Buildable-Area Surveys and Clear Unresolved Regulatory Gates' &&
    hasCitedField('flood') &&
    hasCitedField('environmental') &&
    hasCitedField('geotechnical') &&
    hasCitedDoc('doc-1791196149217-bqcubw')
  ) {
    const out: Array<{ label: string; value: string }> = [];
    if (hasWorkStages) {
      out.push({ label: 'Phase', value: 'Pre-FID Diligence (Work Stage 1–2)' });
    }
    out.push({ label: 'Suggested priority', value: 'Critical (P0 Pre-FID Gate — suggestion)' });
    out.push({
      label: 'Suggested role (unassigned)',
      value: 'Civil / Survey Lead, Environmental & Land-Use Counsel (suggestion)',
    });
    out.push({
      label: 'Deliverable',
      value:
        'ALTA/topographic survey, USACE wetland delineation, FEMA Zone A flood study, Phase I ESA, borehole geotech report, and county/WDEQ/PSC determinations',
    });
    out.push({
      label: 'Dependency',
      value: 'Parcel field-survey access & pre-application filings with Laramie County Planning, WDEQ, and Wyoming PSC',
    });
    out.push({
      label: 'Decision unlocked',
      value: 'Verified net buildable acreage, foundation design basis, and zoning/air/utility permitting clearance for FID',
    });
    out.push({ label: 'Status', value: 'Open — Pre-FID Gate' });
    return out;
  }

  return [];
}

export function parseRecommendationForCard(
  rec: Recommendation,
  enableConciseSplit = true,
  context?: RecommendationPresentationContext,
): ParsedRecommendationCard {
  const recAny = rec as unknown as Record<string, unknown>;
  const structuredMeta: Array<{ label: string; value: string }> = [];
  const metaKeys: Array<[string, string]> = [
    ['phase', 'Phase'],
    ['priority', 'Priority'],
    ['owner', 'Owner'],
    ['proposedResponsibleRole', 'Suggested role (unassigned)'],
    ['deliverable', 'Deliverable'],
    ['dependency', 'Dependency'],
    ['decisionUnlocked', 'Decision unlocked'],
    ['status', 'Status'],
  ];
  const seenLabels = new Set<string>();
  const hasRecordedOwner = typeof recAny.owner === 'string' && recAny.owner.trim().length > 0;
  for (const [key, label] of metaKeys) {
    if (key === 'proposedResponsibleRole' && hasRecordedOwner) continue;
    const val = recAny[key];
    if (typeof val === 'string' && val.trim() && !seenLabels.has(label)) {
      seenLabels.add(label);
      structuredMeta.push({ label, value: val.trim() });
    }
  }

  const rawDesc = dedupeRefTokensInText((rec.description ?? '').trim());
  const rawRationale = dedupeRefTokensInText((rec.rationale ?? '').trim());

  if (
    structuredMeta.length === 0 &&
    enableConciseSplit &&
    context &&
    (rawRationale.length > 0 || rawDesc.length > 260) &&
    (rec.type || rec.title)
  ) {
    structuredMeta.push(...buildSourceBackedLongspurRecommendationMeta(rec, rawDesc, rawRationale, context));
  }

  if (!enableConciseSplit || (rawDesc.length <= 260 && !rawRationale && structuredMeta.length === 0)) {
    return {
      hasSupportingDetail: false,
      summaryDescription: rawDesc,
      supportingDetail: null,
      structuredMeta,
    };
  }

  const sentences = splitRecommendationSentences(rawDesc);
  if (sentences.length <= 1 && !rawRationale) {
    return {
      hasSupportingDetail: false,
      summaryDescription: rawDesc,
      supportingDetail: null,
      structuredMeta,
    };
  }

  const GATING_SENTENCE_RE =
    /\b(?:do\s+not\s+(?:commit|proceed|select|execute|finalize|lock|sign|energize|build|infer|rely|treat|assume)|does\s+not\s+(?:clear|meet|automatically\s+meet|guarantee|establish)\b|cannot\s+be\s+treated\s+as\b|non-binding\b|expected\s+in[- ]service\b|after\s+the\b[^.!?]*\btarget\b|until\b|unless\b|subject\s+to\b|conditional\s+(?:on|upon)\b|contingent\s+(?:on|upon)\b|provided\s+that\b|before\s+(?:committing|proceeding|final\s+selection|fid|cod|execution|locking|construction|foundation|procurement)\b|prerequisite\s+gate|must\s+be\s+(?:independently\s+)?verified|requires?\s+(?:a\s+|an\s+|independent\s+|executed\s+|binding\s+|firm\s+|contracted\s+)?(?:verification|precedent|agreement|contract|study|survey|delineation|determination|ruling|permit|clearance)\b|defer(?:ring)?\s+final\s+selection|pending\s+(?:independent\s+)?verification)/i;

  const summaryParts: string[] = sentences.length > 0 ? [sentences[0]!.trim()] : [];
  let restStartIdx = sentences.length > 0 ? 1 : 0;
  if (summaryParts.length > 0 && summaryParts[0]!.length < 110 && sentences.length > 2) {
    summaryParts.push(sentences[1]!.trim());
    restStartIdx = 2;
  }

  const deferredSentences: string[] = [];
  for (const sentence of sentences.slice(restStartIdx)) {
    const trimmed = sentence.trim();
    if (!trimmed) continue;
    if (GATING_SENTENCE_RE.test(trimmed)) {
      summaryParts.push(trimmed);
    } else {
      deferredSentences.push(trimmed);
    }
  }

  let remainingRationale = rawRationale;
  if (rawRationale) {
    const rationaleSentences = splitRecommendationSentences(rawRationale);
    const deferredRationale: string[] = [];
    for (const rSentence of rationaleSentences) {
      const trimmedR = rSentence.trim();
      if (!trimmedR) continue;
      if (GATING_SENTENCE_RE.test(trimmedR)) {
        if (!summaryParts.includes(trimmedR)) {
          summaryParts.push(trimmedR);
        }
      } else {
        deferredRationale.push(trimmedR);
      }
    }
    if (summaryParts.length === 0 && deferredRationale.length > 0) {
      summaryParts.push(deferredRationale.shift()!);
    }
    remainingRationale = deferredRationale.join(' ').trim();
  }

  const summarySentences = summaryParts.filter(Boolean).join(' ').trim();

  const allRefs = extractUniqueRefs(`${rawDesc} ${rawRationale}`);
  const summaryHasAllRefs = !allRefs || allRefs.split(' ').every((r) => summarySentences.includes(r));
  const summaryWithRefs = summaryHasAllRefs
    ? summarySentences
    : dedupeRefTokensInText(`${summarySentences} ${allRefs}`.trim());

  const remainingDesc = deferredSentences.join(' ').trim();
  const supportingParts = [remainingDesc, remainingRationale].filter(Boolean);

  if (supportingParts.length === 0) {
    return {
      hasSupportingDetail: false,
      summaryDescription: summaryWithRefs || rawDesc,
      supportingDetail: null,
      structuredMeta,
    };
  }

  return {
    hasSupportingDetail: true,
    summaryDescription: summaryWithRefs,
    supportingDetail: supportingParts.join(' '),
    structuredMeta,
  };
}

export function reportHasStructuredActionPlanDetail(
  developmentPathway: Array<{ phase?: string; title?: string; description?: string }> | undefined,
  recommendations: Recommendation[] | undefined,
): boolean {
  const hasPhaseDisclosure = (developmentPathway ?? []).some(
    (p) => parseActionPlanPhaseDescription(p.description).hasStructuredDisclosure,
  );
  if (hasPhaseDisclosure) return true;
  return (recommendations ?? []).some(
    (r) => Boolean(r.rationale?.trim()) || (r.description?.trim().length ?? 0) > 360,
  );
}


export interface SavedPowerActionReferenceTarget {
  readonly heading: string;
  readonly paragraphs: readonly string[];
}

type SavedPowerActionOverview = Pick<AdaptiveProjectIntentOverviewModel,
  'showModeScorecard' | 'screeningMode' | 'recommendationStatus' | 'intentDrift' | 'powerRecommendation'>;

/** Register only the current, visible saved-power row. The caller owns actual
 * surface visibility; DOCX registers this after emitting that row. No saved
 * report field, recommendation, citation or historical observation is edited. */
export function buildSavedPowerActionReferenceTarget(
  report: SiteRagReport,
  overview: SavedPowerActionOverview,
  options: { visible: boolean; heading: string; isEditing?: boolean },
): SavedPowerActionReferenceTarget | null {
  const quarantine = (report as SiteRagReport & {
    sectionQuarantine?: Record<string, unknown>;
  }).sectionQuarantine;
  if (!options.visible || options.isEditing || report.reportType === 'custom-brief'
    || report.qualityHold || report._metadataOnly || report.isDraft || report.degradedMode
    || quarantine?.projectIntentOverview != null || quarantine?.keyFindings != null
    || report.sectionGuardStatus?.projectIntentOverview != null || report.sectionGuardStatus?.keyFindings != null
    || !overview.showModeScorecard || overview.screeningMode !== 'recommend'
    || !['recommended', 'conditional'].includes(overview.recommendationStatus.state)
    || overview.intentDrift.hasModeDrift || overview.intentDrift.hasInputDrift) return null;
  const paragraphs = overview.powerRecommendation.value.split(/\n\s*\n/)
    .map(paragraph => paragraph.replace(/\s+/g, ' ').trim()).filter(Boolean);
  if (paragraphs.length === 0 || !options.heading.trim()) return null;
  return { heading: options.heading.trim(), paragraphs };
}

/** Reference a complete identical action, optionally preceded by its exact
 * saved title. Whitespace is the only normalization: changed citations,
 * numbers, conditions, punctuation or any unmatched suffix prevent removal.
 * Historical/audit prose is never a reference candidate. */
export function referenceRepeatedSavedPowerAction(
  value: string,
  target: SavedPowerActionReferenceTarget | null,
  savedTitles: readonly string[] = [],
): string {
  if (!target || /\b(?:historical|superseded|non[- ]operative|sanity finding|audit observation)\b/i.test(value)) return value;
  const normalize = (text: string): string => text.replace(/\s+/g, ' ').trim();
  const normalized = normalize(value);
  if (normalized.length < 200) return value;
  for (const paragraph of target.paragraphs) {
    if (paragraph.length < 200) continue;
    const title = savedTitles.find(savedTitle =>
      normalized === normalize(`${savedTitle}: ${paragraph}`));
    if (normalized !== paragraph && title === undefined) continue;
    const reference = `See ${target.heading} — Saved power option for the identical action, conditions and citations.`;
    return title === undefined ? reference : `${title}: ${reference}`;
  }
  return value;
}

/** The full-report caller may point an overview action to the unchanged
 * recommendation that ReferencesSection actually emits in its visible Action
 * Plan appendix. Standalone overviews have no target and keep their full text.
 * Match whole paragraphs only: phase notes and any unique suffix stay visible. */
export function referenceSavedPowerOptionToVisibleActionPlan(
  report: SiteRagReport,
  overview: SavedPowerActionOverview,
  options: { visible: boolean; heading: string; isEditing?: boolean },
): string {
  const original = overview.powerRecommendation.value;
  // The overview preserves confidence annotations and document locators;
  // the appendix's citation renderer may remove them or unresolved markers.
  // Raw equality therefore cannot establish their displayed parity. Keep the
  // complete overview until both renderers demonstrably preserve those forms.
  if (/<!-{2,}\s*confidence\s*:|doc:\/\/|\[\s*(?:Ref\s*:?\s*)?\?\s*\]/i.test(original)) return original;
  if (!buildSavedPowerActionReferenceTarget(report, overview, options)) return original;
  const quarantine = (report as SiteRagReport & {
    sectionQuarantine?: Record<string, unknown>;
  }).sectionQuarantine;
  if (quarantine?.actionPlan != null || quarantine?.appendix != null
    || report.sectionGuardStatus?.actionPlan != null || report.sectionGuardStatus?.appendix != null
    || !reportHasStructuredActionPlanDetail(report.developmentPathway, report.recommendations)) return original;
  const normalize = (value: string): string => value.replace(/\s+/g, ' ').trim();
  return original.split(/(\n\s*\n)/).map(paragraph => {
    const exact = normalize(paragraph);
    if (exact.length < 200 || /\b(?:historical|superseded|non[- ]operative|sanity finding|audit observation)\b/i.test(exact)) return paragraph;
    const index = (report.recommendations ?? []).findIndex(rec => {
      if (!rec.title?.trim() || normalize(rec.description ?? '') !== exact) return false;
      const parsed = parseRecommendationForCard(rec, true, {
        siteId: report.siteId, reportId: report.id, sedId: report.sedId,
        references: report.references, developmentPathway: report.developmentPathway,
      });
      // The same entry gate is used by ReferencesSection's supporting appendix.
      return parsed.hasSupportingDetail || Boolean(rec.rationale?.trim());
    });
    if (index < 0) return paragraph;
    const title = report.recommendations![index]!.title;
    return `See ${options.heading.trim()} — Action Plan & Pathway, Recommendation ${index + 1}: ${title} for the identical saved action, conditions and citations.`;
  }).join('');
}
